import React from 'react';
import { Download, Share2, X, Copy, Check } from 'lucide-react';

/**
 * EveryBodyWinsList — global widget.
 * Purely presentational: the host page owns reveal/share/download state and
 * platform-link building, this widget just renders the cards + share modal.
 */
const EveryBodyWinsList = ({
  mode,
  teamImage,
  players = [],
  revealed = [],
  sharing,
  onDownload,
  onShare,
  onPlayAgain,
  shareModal,
  onCloseShareModal,
  platformLinks = [],
  copied,
  onCopy
}) => {
  return (
    <>
      {/* Team mode: single reveal card */}
      {mode === 'team' && (
        <div className="mb-4 d-flex justify-content-center">
          <div className="col-12 col-sm-12">
            <div className="ew-player-card position-relative rounded-3 overflow-hidden">
              {teamImage?.image && <img src={teamImage.image} alt={teamImage.name || 'Reveal'} className="ew-player-img" />}
              <div className={`ew-veil${revealed[0] ? ' ew-veil--revealed' : ''}`}>
                <div className="ew-veil__icon">⚽</div>
                <div className="ew-veil__tier">Your Club</div>
              </div>
              {revealed[0] && <div className="ew-unveiled-badge">UNVEILED!</div>}
              {teamImage?.name && <div className="ew-player-name">{teamImage.name}</div>}
              {revealed[0] && teamImage?.image && (
                <div className="ew-action-bar">
                  <button
                    className="ew-action-btn ew-action-btn--share"
                    title="Share"
                    aria-label="Share"
                    onClick={() => onShare?.(0)}
                    disabled={sharing === 0}
                  >
                    {sharing === 0 ? <span className="ew-action-spinner" /> : <Share2 size={14} strokeWidth={2.5} />}
                  </button>
                </div>
              )}
            </div>
            {revealed[0] && teamImage?.image && (
              <button
                className="btn btn-danger rounded-pill w-100 mt-3 py-2 fw-medium d-flex align-items-center justify-content-center gap-2"
                style={{ backgroundColor: '#8B0000', borderColor: '#8B0000', color: '#fff' }}
                onClick={() => onDownload?.(0)}
              >
                <Download size={16} strokeWidth={2.5} /> Download Image
              </button>
            )}
          </div>
        </div>
      )}

      {/* Tiered mode: 2x2 player grid */}
      {mode !== 'team' && (
        <div className="row g-3 mb-4">
          {[0, 1, 2, 3].map((idx) => {
            const player = players[idx];
            const isRevealed = revealed[idx];
            const tierLabel = `Tier ${idx + 1}`;

            return (
              <div key={idx} className="col-6">
                <div className="ew-player-card position-relative rounded-3 overflow-hidden">
                  {player?.image && <img src={player.image} alt={player?.name || tierLabel} className="ew-player-img" />}
                  <div className={`ew-veil${isRevealed ? ' ew-veil--revealed' : ''}`}>
                    <div className="ew-veil__icon">⚽</div>
                    <div className="ew-veil__tier">{tierLabel}</div>
                  </div>
                  {isRevealed && <div className="ew-unveiled-badge">UNVEILED!</div>}
                  <div className="ew-player-name">{player?.name || ''}</div>
                  {isRevealed && player?.image && (
                    <div className="ew-action-bar">
                      <button
                        className="ew-action-btn ew-action-btn--share"
                        title="Share"
                        aria-label="Share player card"
                        onClick={() => onShare?.(idx)}
                        disabled={sharing === idx}
                      >
                        {sharing === idx ? <span className="ew-action-spinner" /> : <Share2 size={14} strokeWidth={2.5} />}
                      </button>
                    </div>
                  )}
                </div>
                {isRevealed && player?.image && (
                  <button
                    className="btn btn-danger rounded-pill w-100 mt-2 py-2 fw-medium d-flex align-items-center justify-content-center gap-2"
                    style={{ fontSize: '0.8rem', backgroundColor: '#8B0000', borderColor: '#8B0000', color: '#fff' }}
                    onClick={() => onDownload?.(idx)}
                  >
                    <Download size={14} strokeWidth={2.5} /> Download
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {onPlayAgain && (
        <div className="text-center">
          <button className="btn btn-light rounded-pill py-3 fw-medium w-100" onClick={onPlayAgain}>
            Play Again
          </button>
        </div>
      )}

      {/* Fallback share modal */}
      {shareModal && (
        <div className="ew-share-backdrop" onClick={onCloseShareModal}>
          <div className="ew-share-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ew-share-modal__header">
              <span className="ew-share-modal__title">Share {shareModal.playerName}</span>
              <button className="ew-share-modal__close" aria-label="Close" onClick={onCloseShareModal}>
                <X size={18} />
              </button>
            </div>

            <p className="ew-share-modal__copy">{shareModal.shareText}</p>

            <div className="ew-share-platforms">
              {platformLinks.map((p) => (
                <a
                  key={p.key}
                  href={p.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ew-share-platform-btn"
                  style={{ '--platform-color': p.color }}
                >
                  <span className="ew-share-platform-icon">{p.icon}</span>
                  <span className="ew-share-platform-label">{p.label}</span>
                </a>
              ))}
            </div>

            <button
              className="ew-share-copy-btn"
              onClick={() => onCopy?.(`${shareModal.shareText}\n${shareModal.shareUrl}`)}
            >
              {copied ? <><Check size={15} /> Copied!</> : <><Copy size={15} /> Copy message</>}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default EveryBodyWinsList;
